/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, call, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  AssetOption,
  AssetOptionType,
  AsyncActionStateNoData,
  RecordingOption,
  XError,
} from '@wit/vtv-x-foundation-lib';
import RunRecordingOption from 'store/ducks/programInfo/actions/runOptionsRecordings';
import { Creators as PIBarOptions } from 'store/ducks/programInfo/bar/options';

export enum ActionType {
  REQUEST = 'programInfo/bar/runOption',
  SUCCESS = 'programInfo/bar/runOption/SUCCESS',
  FAIL = 'programInfo/bar/runOption/FAIL',
}

// ACTIONS
type RunOption = ActionRequest<ActionType.REQUEST, AssetOption>;
type RunOptionSuccess = ActionSuccess<ActionType.SUCCESS>;
type RunOptionFail = ActionFail<ActionType.FAIL>;

type Actions = RunOption | RunOptionFail | RunOptionSuccess;

const Actions = {
  request: (option: AssetOption): RunOption => ({
    type: ActionType.REQUEST,
    meta: option,
  }),
  success: (): RunOptionSuccess => ({
    type: ActionType.SUCCESS,
  }),
  fail: (error: XError): RunOptionFail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const Creators = { runOption: Actions.request };

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState): boolean => {
    return state.programInfo.bar.runOptions.processing === true;
  },
  isProcessed: (state: StoreState): boolean => {
    return state.programInfo.bar.runOptions.processed === true;
  },
  getError: (state: StoreState): XError | undefined => {
    return state.programInfo.bar.runOptions.error;
  },
};

// SAGAS
function* requestSaga(action: RunOption): SagaIterator {
  const option = action.meta;

  try {
    switch (option?.type) {
      case AssetOptionType.RECORDING:
        yield call(RunRecordingOption, option as RecordingOption);
        break;
      default:
        break;
    }

    if (option.asset) {
      yield put(PIBarOptions.request(option.asset));
    }

    yield put(Actions.success());
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLatest(ActionType.REQUEST, requestSaga)]);
}

// REDUCER
const initialState: AsyncActionStateNoData = {
  processing: false,
  processed: false,
  error: undefined,
};

export default function PIBar(state = initialState, action: Actions): AsyncActionStateNoData {
  switch (action.type) {
    case ActionType.REQUEST: {
      return {
        processing: true,
        processed: false,
        error: undefined,
      };
    }
    case ActionType.SUCCESS: {
      return {
        processing: false,
        processed: true,
        error: undefined,
      };
    }
    case ActionType.FAIL: {
      return {
        processing: false,
        processed: true,
        error: action.error,
      };
    }
    default:
      return state;
  }
}
